import { useEffect, useState } from "react";
import { toast } from "sonner";
import { OrderTimeline } from "@/components/portal/OrderTimeline";
import { OrderActions } from "@/components/portal/OrderActions";
import { api, errMsg, fmtDate, img, inr } from "@/lib/api";

export function OrdersPanel({ isAdmin, manufacturers = [], onRead }) {
  const [orders, setOrders] = useState([]);
  const [selId, setSelId] = useState(null);
  const unreadKey = isAdmin ? "unread_admin" : "unread_manufacturer";

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const open = async (o) => {
    setSelId(o.id);
    if (o[unreadKey]) {
      await api.post(`/orders/${o.id}/read`);
      setOrders((list) => list.map((x) => (x.id === o.id ? { ...x, [unreadKey]: false } : x)));
      onRead?.();
    }
  };
  const replace = (o) => setOrders((list) => list.map((x) => (x.id === o.id ? o : x)));
  const confirm = async (eid) => {
    try {
      const { data } = await api.post(`/orders/${selId}/confirm/${eid}`);
      replace(data);
      toast.success("Update confirmed - customer status changed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const sel = orders.find((o) => o.id === selId);

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="orders-panel">
      <div className="lg:col-span-2 space-y-2 max-h-[75vh] overflow-y-auto scrollbar-thin pr-1">
        {orders.length === 0 && <p className="text-[#6E6558] text-sm" data-testid="orders-empty">No orders yet.</p>}
        {orders.map((o) => (
          <button key={o.id} onClick={() => open(o)} className={`w-full text-left rounded-sm border p-4 transition-colors ${selId === o.id ? "border-[#D4AF37]/60 bg-[#1D1915]" : "border-[#D4AF37]/10 bg-[#14110E] hover:bg-[#1D1915]"}`} data-testid={`order-row-${o.order_number}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-display text-sm flex items-center gap-2">{o[unreadKey] && <span className="h-2 w-2 rounded-full bg-[#E06D3B]" data-testid="order-unread-dot" />}{o.order_number}</span>
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37]">{o.status}</span>
            </div>
            <p className="text-xs text-[#6E6558] mt-1">{o.customer.name} · {inr(o.total)} · {fmtDate(o.created_at)}</p>
            {isAdmin && <p className="text-xs text-[#A89F91] mt-1">{o.manufacturer_name ? `→ ${o.manufacturer_name}` : "Unassigned"} · shown to customer: {o.public_status}</p>}
          </button>
        ))}
      </div>
      <div className="lg:col-span-3">
        {!sel ? (
          <p className="font-serif-it text-xl text-[#6E6558]">Select an order to see its story.</p>
        ) : (
          <div className="rounded-sm border border-[#D4AF37]/15 bg-[#14110E] p-6 space-y-6" data-testid="order-detail">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-2xl" data-testid="order-detail-number">{sel.order_number}</h3>
                <p className="text-xs text-[#6E6558]">{fmtDate(sel.created_at)}</p>
              </div>
              <div className="text-right text-xs">
                <p className="uppercase tracking-[0.15em] text-[#D4AF37]" data-testid="order-detail-status">{sel.status}</p>
                <p className="text-[#A89F91] mt-1" data-testid="order-public-status">Customer sees: {sel.public_status}</p>
              </div>
            </div>
            {isAdmin && (
              <div className="text-sm text-[#A89F91]" data-testid="order-customer">
                <p className="text-[#F5EFE6]">{sel.customer.name} · {sel.customer.phone}</p>
                <p>{sel.customer.email}</p>
                <p className="whitespace-pre-line">{sel.customer.address}</p>
              </div>
            )}
            <div className="space-y-2">
              {sel.items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-3 text-sm">
                  <img src={img(i.image_url)} alt="" className="h-12 w-10 object-cover rounded" />
                  <span className="flex-1 truncate">{i.name} × {i.qty}</span>
                  <span className="text-[#F3E5AB]">{inr(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="zari-divider" />
            <OrderTimeline timeline={sel.timeline} onConfirm={isAdmin ? confirm : null} />
            <div className="zari-divider" />
            <OrderActions key={sel.id} order={sel} isAdmin={isAdmin} manufacturers={manufacturers} onChange={replace} />
          </div>
        )}
      </div>
    </div>
  );
}
